import { legalTermsApi, orderTaxEstimatesApi, ordersApi } from "client/api-v2";
import type {
  AcceptOrderDto,
  CreateOrderDto,
  OrderContactsDto,
  OrderDto,
  OrderFulfillmentDto,
  OrderHistoryEntryDto,
  OrderRateCardOperationsDto,
  OrderResellersDto,
  OrderStatus,
  OrderSummaryDto,
  OrderTaxEstimatePreviewResultDto,
  PreapprovedLegalTermDto,
  PreviewOrderPriceDto,
  PriceBreakdown,
  QuoteDocumentContent,
  QuoteTargetSearchResultDto,
  RateCardTermsDto,
  SalesMotion,
  SetInternalConfirmationDto,
  UpdateOrderDto,
} from "client/api-v2";

export async function getOrders(
  params: {
    status?: OrderStatus;
    salesMotion?: SalesMotion;
    salesRep?: string;
    selfServeOnly?: boolean;
    reviewRequired?: boolean;
    search?: string;
    includeArchived?: boolean;
    page?: number;
    pageSize?: number;
  },
  signal: AbortSignal
): Promise<{ items: OrderSummaryDto[]; totalItems: number }> {
  const response = await ordersApi.getV2Orders({
    query: {
      status: params.status,
      salesMotion: params.salesMotion,
      salesRep: params.salesRep,
      selfServeOnly: params.salesRep === undefined ? params.selfServeOnly : undefined,
      reviewRequired: params.reviewRequired,
      search: params.search,
      includeArchived: params.includeArchived,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
    },
    signal,
  });

  return {
    items: response.data.items || [],
    totalItems: response.data.totalItems || 0,
  };
}

export async function listOrderSalesReps(signal: AbortSignal): Promise<string[]> {
  const response = await ordersApi.getV2OrdersSalesReps({ signal });
  return response.data;
}

export async function getOrder(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.getV2OrdersByOdNumber({ path: { odNumber } });
  return response.data;
}

export async function getOrderDocument(odNumber: string): Promise<QuoteDocumentContent> {
  const response = await ordersApi.getV2OrdersByOdNumberDocument({ path: { odNumber } });
  return response.data;
}

export async function getOrderHistory(odNumber: string): Promise<OrderHistoryEntryDto[]> {
  const response = await ordersApi.getV2OrdersByOdNumberHistory({ path: { odNumber } });
  return response.data;
}

export async function searchQuoteTargets(term: string): Promise<QuoteTargetSearchResultDto> {
  const response = await ordersApi.getV2OrdersSearch({ query: { term } });
  return response.data;
}

export async function createOrder(dto: CreateOrderDto): Promise<OrderDto> {
  const response = await ordersApi.postV2Orders({ body: dto });
  return response.data;
}

export async function updateOrder(odNumber: string, dto: UpdateOrderDto): Promise<OrderDto> {
  const response = await ordersApi.putV2OrdersByOdNumber({ path: { odNumber }, body: dto });
  return response.data;
}

export async function publishQuote(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberMakeLive({ path: { odNumber } });
  return response.data;
}

export async function retractQuote(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberRetract({ path: { odNumber } });
  return response.data;
}

export async function cancelQuote(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberCancel({ path: { odNumber } });
  return response.data;
}

export async function reopenQuote(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberReopen({ path: { odNumber } });
  return response.data;
}

export async function archiveQuote(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberArchive({ path: { odNumber } });
  return response.data;
}

export async function setInternalConfirmation(odNumber: string, dto: SetInternalConfirmationDto): Promise<OrderDto> {
  const response = await ordersApi.putV2OrdersByOdNumberInternalConfirmation({ path: { odNumber }, body: dto });
  return response.data;
}

export async function acceptOrderOnBehalf(odNumber: string, evidence: AcceptOrderDto): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberAccept({ path: { odNumber }, body: evidence });
  return response.data;
}

export async function retryOrderBilling(_odNumber: string): Promise<void> {
  throw new Error("Retrying billing setup is not supported: no backend route exists for it.");
}

export async function getOrderContacts(odNumber: string): Promise<OrderContactsDto> {
  const response = await ordersApi.getV2OrdersByOdNumberContacts({ path: { odNumber } });
  return response.data;
}

export async function listResellers(search?: string): Promise<OrderResellersDto> {
  const response = await ordersApi.getV2OrdersResellers({ query: { search } });
  return response.data;
}

export async function createOrderTaxEstimate(odNumber: string): Promise<OrderTaxEstimatePreviewResultDto> {
  const response = await orderTaxEstimatesApi.postV2OrdersByOdNumberTaxEstimate({ path: { odNumber } });
  return response.data;
}

export async function getOrderFulfillment(odNumber: string): Promise<OrderFulfillmentDto> {
  const response = await ordersApi.getV2OrdersByOdNumberFulfillment({ path: { odNumber } });
  return response.data;
}

export async function listPreapprovedLegalTerms(includeWithdrawn?: boolean): Promise<PreapprovedLegalTermDto[]> {
  const response = await legalTermsApi.getV2LegalTerms({ query: { includeWithdrawn } });
  return response.data;
}

export async function previewOrderPrice(
  odNumber: string,
  quantities: PreviewOrderPriceDto["quantities"],
  signal?: AbortSignal
): Promise<PriceBreakdown> {
  const response = await ordersApi.postV2OrdersByOdNumberPricePreview({
    path: { odNumber },
    body: { quantities },
    signal,
  });
  return response.data;
}

export async function getOrderRateCardOperations(odNumber: string): Promise<OrderRateCardOperationsDto> {
  const response = await ordersApi.getV2OrdersByOdNumberRateCardOperations({ path: { odNumber } });
  return response.data;
}

export async function cloneOrderRateCard(odNumber: string, terms: RateCardTermsDto): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberRateCardClone({ path: { odNumber }, body: terms });
  return response.data;
}

export async function assignPlanDefaultRateCard(odNumber: string): Promise<OrderDto> {
  const response = await ordersApi.postV2OrdersByOdNumberRateCardPlanDefault({ path: { odNumber } });
  return response.data;
}
